import { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";

import { getNonProfitById } from "../services/graphqlService";

function toExternalUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function DetailRow({ label, value, href }) {
  if (value === undefined || value === null || value === "") return null;
  const display = Array.isArray(value)
    ? value
        .map((v) => (typeof v === "object" ? JSON.stringify(v) : v))
        .join(", ")
    : typeof value === "object"
    ? JSON.stringify(value, null, 2)
    : String(value);
  const linkHref = href ? toExternalUrl(href) : "";
  return (
    <Grid item xs={12} sm={6}>
      <SoftBox
        py={1}
        px={1.5}
        sx={{
          borderRadius: 1,
          bgcolor: "grey.50",
        }}
      >
        <SoftTypography
          variant="caption"
          color="text"
          fontWeight="bold"
          display="block"
        >
          {label}
        </SoftTypography>
        {linkHref ? (
          <SoftTypography
            component="a"
            href={linkHref}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            fontWeight="regular"
            color="info"
            sx={{
              textDecoration: "underline",
              wordBreak: "break-all",
              "&:hover": { opacity: 0.85 },
            }}
          >
            {display}
          </SoftTypography>
        ) : (
          <SoftTypography variant="body2" fontWeight="regular" color="text">
            {display}
          </SoftTypography>
        )}
      </SoftBox>
    </Grid>
  );
}

function SectionTitle({ children }) {
  return (
    <SoftTypography
      variant="button"
      fontWeight="bold"
      color="text"
      sx={{
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        mb: 1,
        display: "block",
      }}
    >
      {children}
    </SoftTypography>
  );
}

function ViewNonprofitModal({
  open,
  onClose,
  nonprofitId,
  idToken,
  prefetchedRecord,
}) {
  const [nonprofit, setNonprofit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setNonprofit(null);
      setError(null);
      return;
    }
    if (prefetchedRecord) {
      setNonprofit(prefetchedRecord);
      setLoading(false);
      setError(null);
    }
  }, [open, prefetchedRecord]);

  useEffect(() => {
    if (!open || prefetchedRecord) {
      return;
    }
    if (!nonprofitId || !idToken) {
      setNonprofit(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getNonProfitById(nonprofitId, idToken)
      .then((res) => {
        if (cancelled) return;
        setNonprofit(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load non-profit");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, nonprofitId, idToken, prefetchedRecord]);

  const handleClose = () => {
    setNonprofit(null);
    setError(null);
    onClose();
  };

  const np = nonprofit || {};
  const address = Array.isArray(np.address) && np.address.length > 0 ? np.address[0] : {};
  const operationalStates = Array.isArray(np.operationalStates) ? np.operationalStates : [];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiBackdrop-root": { bgcolor: "rgba(0,0,0,0.4)" },
      }}
    >
      <Card
        sx={{
          width: "90%",
          maxWidth: 720,
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: 24,
          borderRadius: 2,
        }}
      >
        <SoftBox p={3}>
          {/* Header */}
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <SoftBox>
              <SoftTypography variant="h4" fontWeight="bold" color="dark">
                {np.name || "—"}
              </SoftTypography>
              {np.category && (
                <SoftBox mt={0.5}>
                  <SoftBadge
                    variant="contained"
                    badgeContent={np.category.replace(/-/g, " ")}
                    color="success"
                    size="xs"
                    container
                  />
                </SoftBox>
              )}
            </SoftBox>
            <SoftButton
              variant="text"
              color="secondary"
              iconOnly
              onClick={handleClose}
            >
              <Icon>close</Icon>
            </SoftButton>
          </SoftBox>

          <Divider sx={{ mb: 2 }} />

          {loading && (
            <SoftBox display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </SoftBox>
          )}

          {error && (
            <SoftBox mt={1} mb={2}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </SoftBox>
          )}

          {!loading && !error && nonprofit && (
            <SoftBox>
              {/* Contact */}
              <SectionTitle>Contact</SectionTitle>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <DetailRow label="EIN" value={np.ein} />
                <DetailRow label="Email" value={np.email} />
                <DetailRow label="Phone" value={np.phone} />
                <DetailRow label="Website" value={np.url} href={np.url} />
              </Grid>

              {/* Address */}
              <SectionTitle>Address</SectionTitle>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <DetailRow label="Street Address" value={address.address} />
                <DetailRow label="City" value={address.city || np.city} />
                <DetailRow label="State" value={address.state || np.state} />
              </Grid>

              {/* Operational States */}
              <SoftBox sx={{ mb: 3 }}>
                <SectionTitle>Operational States</SectionTitle>
                {operationalStates.length > 0 ? (
                  <List
                    dense
                    disablePadding
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      bgcolor: "grey.50",
                    }}
                  >
                    {operationalStates.map((state, i) => (
                      <ListItem
                        key={i}
                        divider={i < operationalStates.length - 1}
                        sx={{ py: 1.25, px: 2 }}
                      >
                        <ListItemText
                          primary={
                            <SoftTypography variant="body2" color="text">
                              {state}
                            </SoftTypography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <SoftBox
                    py={2}
                    px={2}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      bgcolor: "grey.50",
                    }}
                  >
                    <SoftTypography variant="body2" color="secondary">
                      No operational states
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>

             
            </SoftBox>
          )}

          {!loading && !error && !nonprofit && open && nonprofitId && (
            <SoftBox py={5} textAlign="center">
              <SoftTypography variant="body2" color="text">
                No non-profit data returned.
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>
      </Card>
    </Modal>
  );
}

export default ViewNonprofitModal;
